<template>
  <!-- Rejected: read-only review + reassurance (mirrors business verification). -->
  <div v-if="readOnly" class="surface">
    <div v-if="layout !== 'page'" class="sHead"><span class="sTitle">Spam-label protection</span></div>
    <div class="rejectBanner">
      <AppIcon name="about" :size="16" />
      <span>We’re reviewing the details — we’ll be in touch.</span>
    </div>
    <div class="reviewGroup">
      <span class="reviewGroupTitle">Calling profile</span>
      <p class="reviewLine">{{ profileSummary }}</p>
    </div>
    <div class="reviewGroup">
      <span class="reviewGroupTitle">Your description</span>
      <p class="reviewLine">{{ form.notes || '—' }}</p>
    </div>
  </div>

  <WizardChrome
    v-else
    title="Spam-label protection"
    :step-label="stepLabel"
    :current-step="step"
    :total-steps="2"
    :layout="layout"
  >
    <template v-if="step === 1">
      <FormField label="What are your calls mostly for?" :error="errors.useCase">
        <select v-model="form.useCase" class="tinput" @change="clearErr('useCase')">
          <option value="">Select…</option>
          <option v-for="o in VOICE_INTEGRITY_USE_CASE_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </FormField>
      <FormField label="Employees" :error="errors.businessEmployeeCount">
        <input v-model="form.businessEmployeeCount" class="tinput" type="number" min="1" @input="clearErr('businessEmployeeCount')" />
      </FormField>
      <FormField label="Calls on a typical day" :error="errors.averageBusinessDayCallVolume" hint="Your best estimate is fine.">
        <input v-model="form.averageBusinessDayCallVolume" class="tinput" type="number" min="1" @input="clearErr('averageBusinessDayCallVolume')" />
      </FormField>
      <FormField label="Describe your calling in your own words" optional hint="Optional, but it helps a lot.">
        <textarea v-model="form.notes" class="ttextarea" rows="6" :placeholder="notesPlaceholder" />
      </FormField>
      <div class="whyCard">
        <span class="whyTitle">A good description covers</span>
        <p class="whyBody">
          Who’s calling and who you call · why they’d expect your call (they asked for a demo,
          they’re a customer) · that a person, not a machine, places each call · how you handle
          “don’t call me again.”
        </p>
      </div>
    </template>

    <template v-else>
      <div class="reviewGroup">
        <div class="reviewGroupHead">
          <span class="reviewGroupTitle">Calling profile</span>
          <button type="button" class="editLink" @click="goToStep(1)">Edit</button>
        </div>
        <p class="reviewLine">{{ profileSummary }}</p>
      </div>
      <div class="reviewGroup">
        <div class="reviewGroupHead">
          <span class="reviewGroupTitle">Your description</span>
          <button type="button" class="editLink" @click="goToStep(1)">Edit</button>
        </div>
        <p class="reviewLine">{{ form.notes || '—' }}</p>
      </div>
      <p class="lockNote">
        After you submit, details lock while carriers review — typically 1–2 days. Your numbers keep
        working the whole time; protection switches on by itself once approved.
      </p>
      <p v-if="submitError" class="errorMsg">{{ submitError }}</p>
    </template>

    <template #footer>
      <button v-if="step === 1" type="button" class="btn primary" @click="onContinue">Continue</button>
      <button v-else type="button" class="btn primary" :disabled="submitting" @click="onSubmit">
        {{ submitting ? 'Submitting…' : 'Submit' }}
      </button>
      <button v-if="step === 1 && layout !== 'page'" type="button" class="btn secondary" @click="$emit('home')">Do this later</button>
      <button v-else type="button" class="btn secondary" @click="step = 1">Back</button>
    </template>
  </WizardChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import AppIcon from '../../../../components/AppIcon.vue'
import WizardChrome from './WizardChrome.vue'
import FormField from './FormField.vue'
import { useTelephony } from '../../../composables/useTelephony'
import { VOICE_INTEGRITY_USE_CASE_OPTIONS } from './telephonyOptions'
import { validateVoiceIntegrity, type FieldErrors } from './telephonyValidation'
import {
  getTelephonyDraft,
  putTelephonyDraft,
  submitVoiceIntegrity,
} from '../../../../services/dialerApi'

withDefaults(defineProps<{ layout?: 'drawer' | 'page' }>(), { layout: 'drawer' })
const emit = defineEmits<{ (e: 'home'): void }>()
const telephony = useTelephony()

const form = reactive({
  useCase: '',
  businessEmployeeCount: '' as string | number,
  averageBusinessDayCallVolume: '' as string | number,
  notes: '',
})

const step = ref(1)
const errors = ref<FieldErrors>({})
const submitting = ref(false)
const submitError = ref('')
const hydrated = ref(false)
const businessName = ref('')

const state = computed(() => telephony.projection.value?.voice_integrity.state ?? 'not_started')
const readOnly = computed(() => state.value === 'rejected')

const stepLabel = computed(() =>
  step.value === 1 ? 'Step 1 of 2 · Your calling profile' : 'Step 2 of 2 · Review & submit',
)

const profileSummary = computed(() => {
  const uc = VOICE_INTEGRITY_USE_CASE_OPTIONS.find((o) => o.value === form.useCase)?.label || '—'
  const emp = form.businessEmployeeCount || '—'
  const vol = form.averageBusinessDayCallVolume || '—'
  return `${uc} · ${emp} employees · ~${vol} calls a day`
})

function clearErr(key: string) {
  if (errors.value[key]) {
    const next = { ...errors.value }
    delete next[key]
    errors.value = next
  }
}

function notesTemplate(business: string): string {
  const name = business || 'Our company'
  return (
    `e.g. ${name} is a B2B company. Our in-house sales reps place human-initiated calls from our own ` +
    `numbers to business contacts who requested a demo, downloaded a resource, or are existing ` +
    `customers. Every call identifies ${name} and the rep, references the contact’s prior interest, ` +
    `and honors opt-out requests immediately. We don’t use prerecorded messages.`
  )
}
const notesPlaceholder = computed(() => (hydrated.value ? notesTemplate(businessName.value) : ''))

async function saveDraft() {
  if (!hydrated.value) return
  try {
    await putTelephonyDraft('voice_integrity', { ...form })
  } catch {
    /* best-effort autosave */
  }
}

let autosaveTimer: ReturnType<typeof setTimeout> | null = null
watch(
  form,
  () => {
    if (!hydrated.value) return
    if (autosaveTimer) clearTimeout(autosaveTimer)
    autosaveTimer = setTimeout(saveDraft, 800)
  },
  { deep: true },
)

function goToStep(n: number) {
  void saveDraft()
  step.value = n
}

function onContinue() {
  const errs = validateVoiceIntegrity(form)
  if (Object.keys(errs).length) {
    errors.value = errs
    return
  }
  errors.value = {}
  void saveDraft()
  step.value = 2
}

async function onSubmit() {
  const errs = validateVoiceIntegrity(form)
  if (Object.keys(errs).length) {
    errors.value = errs
    step.value = 1
    return
  }
  submitting.value = true
  submitError.value = ''
  try {
    await saveDraft()
    await submitVoiceIntegrity()
    await telephony.refresh()
    emit('home')
  } catch {
    submitError.value = 'We couldn’t submit right now — please try again.'
  } finally {
    submitting.value = false
  }
}

onMounted(async () => {
  try {
    const bp = await getTelephonyDraft('business_profile')
    const d = (bp?.data || {}) as Record<string, unknown>
    businessName.value = String(d.businessName || d.legalBusinessName || '')
  } catch {
    /* no business draft */
  }
  try {
    const draft = await getTelephonyDraft('voice_integrity')
    const data = (draft?.data || {}) as Record<string, unknown>
    if (typeof data.useCase === 'string') form.useCase = data.useCase
    if (data.businessEmployeeCount != null) form.businessEmployeeCount = data.businessEmployeeCount as number
    if (data.averageBusinessDayCallVolume != null)
      form.averageBusinessDayCallVolume = data.averageBusinessDayCallVolume as number
    if (typeof data.notes === 'string') form.notes = data.notes
  } catch {
    /* no VI draft */
  }
  hydrated.value = true
})

onBeforeUnmount(() => {
  if (autosaveTimer) clearTimeout(autosaveTimer)
  void saveDraft()
})
</script>

<style scoped>
.surface { display: flex; flex-direction: column; gap: 16px; }
.sHead { display: flex; flex-direction: column; gap: 8px; }
.sTitle { font-family: var(--font-display); font-size: 22px; font-weight: var(--fw-semibold); letter-spacing: -0.01em; }

.ttextarea {
  width: 100%; padding: 10px 12px; border: 1px solid var(--color-border-strong); border-radius: var(--radius-md);
  background: var(--color-bg-surface); color: var(--color-text-primary); font-family: var(--font-sans);
  font-size: var(--fs-sm); line-height: var(--lh-loose); resize: vertical; min-height: 120px;
}
.ttextarea:focus { outline: none; border-color: var(--color-accent); box-shadow: var(--shadow-focus); }
.ttextarea::placeholder { color: var(--color-text-tertiary); }

.whyCard { background: var(--color-bg-subtle); border-radius: var(--radius-lg); padding: 16px; display: flex; flex-direction: column; gap: 6px; }
.whyTitle { font-size: var(--fs-sm); font-weight: var(--fw-semibold); }
.whyBody { margin: 0; font-size: var(--fs-xs); line-height: var(--lh-loose); color: var(--color-text-secondary); }

.reviewGroup { display: flex; flex-direction: column; gap: 4px; padding-bottom: var(--space-3); border-bottom: 1px solid var(--color-border-subtle); }
.reviewGroupHead { display: flex; align-items: center; justify-content: space-between; }
.reviewGroupTitle { font-size: var(--fs-xs); font-weight: var(--fw-semibold); letter-spacing: var(--tracking-wide); text-transform: uppercase; color: var(--color-text-tertiary); }
.editLink { border: 0; background: transparent; padding: 0; color: var(--color-accent); font-size: var(--fs-xs); font-weight: var(--fw-medium); cursor: pointer; }
.editLink:hover { text-decoration: underline; }
.reviewLine { margin: 0; font-size: var(--fs-sm); line-height: var(--lh-loose); color: var(--color-text-primary); }
.lockNote { margin: 0; font-size: var(--fs-xs); line-height: var(--lh-normal); color: var(--color-text-tertiary); }
.rejectBanner { display: flex; align-items: center; gap: 10px; padding: 14px 16px; border-radius: var(--radius-lg); background: var(--color-bg-subtle); font-size: var(--fs-sm); color: var(--color-text-secondary); }
.errorMsg { margin: 0; font-size: var(--fs-xs); color: var(--color-danger); }
</style>
